import { useState, useEffect } from 'react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { v4 as uuidv4 } from 'uuid';
import { formatCurrency } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faDownload } from '@fortawesome/free-solid-svg-icons';
import { AppraisalDialog } from './AppraisalDialog';

export const AppraisalsGrid = ({ appraisals, title, onChange }) => {
    const [appraisal, set_appraisal] = useState(null);

    useEffect(() => {
        if (!appraisals || appraisals.length === 0) {
            return;
        }
        appraisals.forEach((f) => {
            f.guid = uuidv4();
        });
    }, [appraisals]);

    const onCurrencyRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatCurrency(e.value, 0);
    };

    const onDownload = async (e, url) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = url;
    };

    const editAppraisal = (appr) => {
        set_appraisal(appr);
    };

    const onDownloadRender = (e) => {
        if (e.data.fileToUpload) {
            return <span>{e.data.fileToUpload.name}</span>;
        }
        if (!e.value) {
            return 'No file available';
        }
        return (
            <>
                <button
                    className='link'
                    type='button'
                    onClick={(event) => onDownload(event, e.value)}
                    title='Download File'
                    style={{ margin: '0' }}
                >
                    <FontAwesomeIcon icon={faDownload} />
                    &nbsp; {e.data.fileName}
                </button>{' '}
            </>
        );
    };

    const onStatusRender = (e) => {
        if (e.data.id === 0) {
            return <span style={{ color: 'orange' }}>Awaiting Save</span>;
        } else if (e.data.fileToUpload) {
            return <span style={{ color: 'orange' }}>Awaiting Upload</span>;
        } else {
            return <span style={{ color: 'darkgreen' }}>Saved</span>;
        }
    };

    const onEditRender = (e) => {
        return (
            <button
                type='button'
                onClick={() => editAppraisal(e.data)}
                className='button'
                title='Edit Appraisal'
                style={{ verticalAlign: 'unset', marginBottom: '0' }}
            >
                <FontAwesomeIcon icon={faPencil} />
            </button>
        );
    };

    const onEditSubmit = (appr) => {
        const newArray = [...appraisals];
        const index = newArray.findIndex((a) => a.guid === appr.guid);
        if (index >= 0) {
            newArray[index] = appr;
        } else {
            newArray.push(appr);
        }
        set_appraisal(null);
        onChange && onChange(newArray);
    };

    const onEditCancel = () => {
        set_appraisal(null);
    };

    const onAddAppraisal = (e) => {
        e.preventDefault();
        const model = {
            id: 0,
            guid: uuidv4(),
            appraisedDate: new Date(),
            url: null,
        };
        set_appraisal(model);
    };

    if (!appraisals) {
        return null;
    }

    return (
        <div style={{ marginTop: '10px' }}>
            <h2 className='decorated'>{title ?? 'Property Appraisals'}</h2>
            <DataGrid
                dataSource={appraisals}
                showBorders={true}
                showRowLines={false}
                showColumnHeaders={true}
                rowAlternationEnabled={true}
                columnMinWidth={50}
                columnAutoWidth={false}
                noDataText='No appraisals found'
                onRowDblClick={(e) => editAppraisal(e.data)}
            >
                <Column
                    dataField='appraisedDate'
                    caption='Date'
                    dataType='date'
                    width={80}
                    sortIndex={0}
                    sortOrder={'desc'}
                />
                <Column
                    dataField='appraisedValue'
                    caption='Value'
                    cellRender={onCurrencyRender}
                    width={120}
                    alignment={'left'}
                />
                <Column
                    caption='Status'
                    cellRender={onStatusRender}
                    width={150}
                    alignment='left'
                />
                <Column
                    dataField={'url'}
                    caption='File'
                    alignment={'left'}
                    cellRender={onDownloadRender}
                />
                <Column
                    caption=''
                    width={60}
                    alignment='center'
                    cellRender={onEditRender}
                />
            </DataGrid>
            <div style={{ width: '100%', marginTop: '10px' }}>
                <button
                    type='button'
                    onClick={(e) => onAddAppraisal(e)}
                    className='button hollow'
                    title='Add file'
                    style={{ margin: '5px', padding: '6px' }}
                >
                    Add New Appraisal
                </button>
            </div>
            <AppraisalDialog
                isOpen={!!appraisal}
                appraisal={appraisal}
                onCancel={onEditCancel}
                onSubmit={onEditSubmit}
            />
        </div>
    );
};
